import { useParams } from 'react-router-dom';
import { Card, Container, Spinner } from '@/components/ui';
import { useAuth } from '@/app/providers/AuthProvider';
import { ProfileWorkspace } from './components/ProfileWorkspace';
import { useProfileByHandle } from './hooks/useProfile';
import { DirectMessageScreen } from '@/features/dm/components/DirectMessageScreen';
import { useDirectConversation } from '@/features/dm/hooks/useDirectMessages';
import { useFriendship } from './hooks/useSocial';

/**
 * Public profile (`/u/:handle`). Read-only for other people; if the handle
 * resolves to the signed-in user it renders the same full-screen editing
 * workspace as `/me`, so there's one self-profile experience either way.
 */
export function ProfileHandlePage() {
  const { handle } = useParams<{ handle: string }>();
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfileByHandle(handle);

  const isSelf = Boolean(profile && user && profile.id === user.id);
  // Only spin up a DM conversation once the two are friends (DMs are gated).
  const { data: friendship } = useFriendship(user?.id, profile?.id);
  const areFriends = Boolean(friendship);
  const { data: conversationId } = useDirectConversation(
    profile && user && !isSelf && areFriends ? profile.id : undefined,
    Boolean(profile && user && !isSelf && areFriends),
  );

  if (isSelf) {
    return (
      <Container className="mx-auto max-w-[1760px] pb-32 pt-20">
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        ) : profile ? (
          <ProfileWorkspace profile={profile} />
        ) : (
          <ProfileNotFound handle={handle} />
        )}
      </Container>
    );
  }

  // Messaging another member uses the exact same surface as the /dm route.
  if (!isLoading && !profile) {
    return (
      <div className="fixed inset-0 z-[120] grid place-items-center bg-[linear-gradient(180deg,rgba(10,10,14,0.18),rgba(10,10,14,0.3))] backdrop-blur-[22px] backdrop-saturate-[1.3]">
        <ProfileNotFound handle={handle} />
      </div>
    );
  }

  return <DirectMessageScreen peer={profile ?? null} conversationId={conversationId} loading={isLoading} />;
}

function ProfileNotFound({ handle }: { handle?: string }) {
  return (
    <Card className="mx-auto max-w-xl p-10 text-center">
      <div className="text-lg font-extrabold text-hi">Profile not found</div>
      <p className="mt-1 text-sm text-mid">
        No one with the handle <span className="text-hi">@{handle}</span> is on WINR yet.
      </p>
    </Card>
  );
}
